import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  ClipboardCheck, 
  PenTool, 
  RotateCcw, 
  CheckCircle, 
  Eye, 
  Save, 
  Download, 
  Send, 
  Loader2,
  Search,
  FolderOpen,
  Calendar,
  User,
  Trash2,
  AlertTriangle,
  Mail,
  Pencil,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminProtocol() {
  const [activeTab, setActiveTab] = useState('create');
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [settings, setSettings] = useState({});
  
  // Protocol form state
  const [protocolData, setProtocolData] = useState({
    projectName: '',
    projectAddress: '',
    projectCity: '',
    workDescription: '',
    acceptanceDate: new Date().toLocaleDateString('de-DE'),
    acceptanceType: 'ohne_vorbehalt',
    defects: '',
    notes: '',
    customerSignature: null,
    contractorSignature: null,
  });
  const [protocolCustomer, setProtocolCustomer] = useState(null);
  const [isSigningCustomer, setIsSigningCustomer] = useState(false);
  const [isSigningContractor, setIsSigningContractor] = useState(false);
  const [isSavingProtocol, setIsSavingProtocol] = useState(false);
  const [showProtocolPreview, setShowProtocolPreview] = useState(false);
  const [protocolPreviewHtml, setProtocolPreviewHtml] = useState('');
  const [isPreviewingProtocol, setIsPreviewingProtocol] = useState(false);
  
  // Archive state
  const [savedProtocols, setSavedProtocols] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState('');
  const [isLoadingArchive, setIsLoadingArchive] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [protocolToDelete, setProtocolToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editEmailDialogOpen, setEditEmailDialogOpen] = useState(false);
  const [protocolToEdit, setProtocolToEdit] = useState(null);
  const [editEmail, setEditEmail] = useState('');

  // Filtered customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const search = customerSearch.toLowerCase();
    return customers.filter(c => 
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.customer_number?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.toLowerCase().includes(search) ||
      c.city?.toLowerCase().includes(search)
    );
  }, [customers, customerSearch]);

  // Filtered protocols based on search
  const filteredProtocols = useMemo(() => {
    if (!archiveSearch.trim()) return savedProtocols;
    const search = archiveSearch.toLowerCase();
    return savedProtocols.filter(p => 
      p.customer_name?.toLowerCase().includes(search) ||
      p.project_name?.toLowerCase().includes(search) ||
      p.project_address?.toLowerCase().includes(search) ||
      p.acceptance_date?.toLowerCase().includes(search)
    );
  }, [savedProtocols, archiveSearch]);

  useEffect(() => {
    fetchCustomers();
    fetchSettings();
    fetchProtocols();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/documents/settings');
      setSettings(response.data || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchProtocols = async () => {
    setIsLoadingArchive(true);
    try {
      const response = await api.get('/admin/protocols/list');
      setSavedProtocols(response.data || []);
    } catch (error) {
      console.error('Error fetching protocols:', error);
    } finally {
      setIsLoadingArchive(false);
    }
  };

  const selectProtocolCustomer = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setProtocolCustomer(customer);
      setProtocolData(prev => ({
        ...prev,
        projectAddress: customer.street || '',
        projectCity: `${customer.postal_code || ''} ${customer.city || ''}`.trim(),
      }));
      setCustomerSearch('');
    }
  };

  // Signature canvas functions
  const initSignatureCanvas = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return null;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;
    
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      if (e.touches) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY
        };
      }
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    };
    
    const startDrawing = (e) => {
      e.preventDefault();
      isDrawing = true;
      const pos = getPos(e);
      lastX = pos.x;
      lastY = pos.y;
    };
    
    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastX = pos.x;
      lastY = pos.y;
    };
    
    const stopDrawing = () => {
      isDrawing = false;
    };
    
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', stopDrawing);
    
    return canvas;
  };

  const saveSignature = (canvasId, type) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    
    const dataUrl = canvas.toDataURL('image/png');
    if (type === 'customer') {
      setProtocolData(prev => ({ ...prev, customerSignature: dataUrl }));
      setIsSigningCustomer(false);
      toast.success('Kundenunterschrift gespeichert');
    } else {
      setProtocolData(prev => ({ ...prev, contractorSignature: dataUrl }));
      setIsSigningContractor(false);
      toast.success('Auftragnehmerunterschrift gespeichert');
    }
  };

  const clearSignature = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const previewProtocol = async () => {
    if (!protocolCustomer) {
      toast.error('Bitte wählen Sie einen Kunden aus');
      return;
    }
    
    setIsPreviewingProtocol(true);
    try {
      const response = await api.post('/admin/protocols/preview', {
        customer_id: protocolCustomer.id,
        customer_name: `${protocolCustomer.first_name} ${protocolCustomer.last_name}`,
        customer_address: protocolCustomer.street,
        customer_city: `${protocolCustomer.postal_code} ${protocolCustomer.city}`,
        project_name: protocolData.projectName,
        project_address: protocolData.projectAddress,
        project_city: protocolData.projectCity,
        work_description: protocolData.workDescription,
        acceptance_date: protocolData.acceptanceDate,
        acceptance_type: protocolData.acceptanceType,
        defects: protocolData.defects,
        notes: protocolData.notes,
        customer_signature: protocolData.customerSignature || '',
        contractor_signature: protocolData.contractorSignature || '',
      });
      setProtocolPreviewHtml(response.data.html);
      setShowProtocolPreview(true);
    } catch (error) {
      console.error('Error previewing protocol:', error);
      toast.error('Fehler beim Laden der Vorschau');
    } finally {
      setIsPreviewingProtocol(false);
    }
  };

  const saveProtocol = async () => {
    if (!protocolCustomer) {
      toast.error('Bitte wählen Sie einen Kunden aus');
      return;
    }
    if (!protocolData.customerSignature) {
      toast.error('Bitte fügen Sie die Kundenunterschrift hinzu');
      return;
    }
    
    setIsSavingProtocol(true);
    try {
      await api.post('/admin/protocols/save', {
        customer_id: protocolCustomer.id,
        customer_name: `${protocolCustomer.first_name} ${protocolCustomer.last_name}`,
        customer_email: protocolCustomer.email,
        customer_address: protocolCustomer.street,
        customer_city: `${protocolCustomer.postal_code} ${protocolCustomer.city}`,
        project_name: protocolData.projectName,
        project_address: protocolData.projectAddress,
        project_city: protocolData.projectCity,
        work_description: protocolData.workDescription,
        acceptance_date: protocolData.acceptanceDate,
        acceptance_type: protocolData.acceptanceType,
        defects: protocolData.defects,
        notes: protocolData.notes,
        customer_signature: protocolData.customerSignature,
        contractor_signature: protocolData.contractorSignature,
      });
      
      toast.success('Abnahmeprotokoll erfolgreich gespeichert');
      fetchProtocols();
      
      // Reset form
      setProtocolData({
        projectName: '',
        projectAddress: '',
        projectCity: '',
        workDescription: '',
        acceptanceDate: new Date().toLocaleDateString('de-DE'),
        acceptanceType: 'ohne_vorbehalt',
        defects: '',
        notes: '',
        customerSignature: null,
        contractorSignature: null,
      });
      setProtocolCustomer(null);
      
      // Switch to archive tab
      setActiveTab('archive');
    } catch (error) {
      console.error('Error saving protocol:', error);
      toast.error('Fehler beim Speichern des Protokolls');
    } finally {
      setIsSavingProtocol(false);
    }
  };

  const downloadProtocolPdf = async (protocolId) => {
    try {
      const response = await api.get(`/admin/protocols/${protocolId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Abnahmeprotokoll_${protocolId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading protocol:', error);
      toast.error('Fehler beim Download');
    }
  };

  const sendProtocolEmail = async (protocolId, customerEmail) => {
    if (!customerEmail) {
      toast.error('Keine E-Mail-Adresse für diesen Kunden hinterlegt');
      return;
    }
    try {
      await api.post(`/admin/protocols/${protocolId}/send-email`);
      toast.success('Protokoll wurde per E-Mail versendet');
    } catch (error) {
      console.error('Error sending protocol email:', error);
      toast.error('Fehler beim E-Mail-Versand');
    }
  };

  const confirmDeleteProtocol = (protocol) => {
    setProtocolToDelete(protocol);
    setDeleteDialogOpen(true);
  };

  const deleteProtocol = async () => {
    if (!protocolToDelete) return;
    
    setIsDeleting(true);
    try {
      await api.delete(`/admin/protocols/${protocolToDelete.id}`);
      toast.success('Protokoll erfolgreich gelöscht');
      fetchProtocols();
      setDeleteDialogOpen(false);
      setProtocolToDelete(null);
    } catch (error) {
      console.error('Error deleting protocol:', error);
      toast.error('Fehler beim Löschen des Protokolls');
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditEmailDialog = (protocol) => {
    setProtocolToEdit(protocol);
    setEditEmail(protocol.customer_email || '');
    setEditEmailDialogOpen(true);
  };

  const saveProtocolEmail = async () => {
    if (!protocolToEdit || !editEmail) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }
    
    setIsSavingProtocol(true);
    try {
      await api.put(`/admin/protocols/${protocolToEdit.id}/email`, {
        customer_email: editEmail
      });
      toast.success('E-Mail-Adresse gespeichert');
      fetchProtocols();
      setEditEmailDialogOpen(false);
      setProtocolToEdit(null);
    } catch (error) {
      console.error('Error saving email:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSavingProtocol(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 md:w-7 md:h-7 text-[#9B1B30]" />
            Abnahmeprotokoll
          </h1>
          <p className="text-sm text-gray-600 mt-1">Protokolle erstellen und verwalten</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="create" className="text-sm" data-testid="protocol-tab-create">
            <PenTool className="w-4 h-4 mr-2" />
            Neues Protokoll
          </TabsTrigger>
          <TabsTrigger value="archive" className="text-sm" data-testid="protocol-tab-archive">
            <FolderOpen className="w-4 h-4 mr-2" />
            Archiv ({savedProtocols.length})
          </TabsTrigger>
        </TabsList>

        {/* Create Protocol Tab */}
        <TabsContent value="create" className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Left Side: Form */}
            <div className="space-y-4">
              {/* Customer Selection with Search */}
              <Card>
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="text-base md:text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-[#9B1B30]" />
                    Kunde auswählen
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0 space-y-4">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Kunde suchen (Name, Nr., E-Mail, Ort)..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="pl-10"
                      data-testid="customer-search-input"
                    />
                  </div>
                  
                  {/* Customer Dropdown */}
                  <Select 
                    value={protocolCustomer?.id || ''} 
                    onValueChange={selectProtocolCustomer}
                  >
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Kunde aus Liste wählen..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          {customerSearch ? 'Keine Kunden gefunden' : 'Keine Kunden vorhanden'}
                        </div>
                      ) : (
                        filteredCustomers.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{c.customer_number} - {c.first_name} {c.last_name}</span>
                              <span className="text-xs text-gray-500">{c.city} {c.email && `• ${c.email}`}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  
                  {protocolCustomer && (
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <p className="font-medium">{protocolCustomer.first_name} {protocolCustomer.last_name}</p>
                      <p className="text-gray-600">{protocolCustomer.street}</p>
                      <p className="text-gray-600">{protocolCustomer.postal_code} {protocolCustomer.city}</p>
                      {protocolCustomer.email && <p className="text-gray-600">{protocolCustomer.email}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Project Details */}
              <Card>
                <CardHeader className="p-4 md:p-6 pb-2">
                  <CardTitle className="text-base">Projekt / Objekt</CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0 space-y-3">
                  <div>
                    <Label className="text-sm">Projektbezeichnung</Label>
                    <Input 
                      value={protocolData.projectName}
                      onChange={(e) => setProtocolData({...protocolData, projectName: e.target.value})}
                      placeholder="z.B. Malerarbeiten Wohnzimmer"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Objektadresse</Label>
                      <Input 
                        value={protocolData.projectAddress}
                        onChange={(e) => setProtocolData({...protocolData, projectAddress: e.target.value})}
                        placeholder="Straße Nr."
                      />
                    </div>
                    <div>
                      <Label className="text-sm">PLZ / Ort</Label>
                      <Input 
                        value={protocolData.projectCity}
                        onChange={(e) => setProtocolData({...protocolData, projectCity: e.target.value})}
                        placeholder="PLZ Ort"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Beschreibung der durchgeführten Arbeiten *</Label>
                    <Textarea 
                      value={protocolData.workDescription}
                      onChange={(e) => setProtocolData({...protocolData, workDescription: e.target.value})}
                      placeholder="Detaillierte Beschreibung aller durchgeführten Arbeiten..."
                      className="min-h-[100px]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Abnahmedatum</Label>
                    <Input 
                      type="text"
                      value={protocolData.acceptanceDate}
                      onChange={(e) => setProtocolData({...protocolData, acceptanceDate: e.target.value})}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Acceptance Type */}
              <Card>
                <CardHeader className="p-4 md:p-6 pb-2">
                  <CardTitle className="text-base">Abnahme</CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0 space-y-3">
                  <div>
                    <Label className="text-sm">Art der Abnahme</Label>
                    <Select 
                      value={protocolData.acceptanceType} 
                      onValueChange={(val) => setProtocolData({...protocolData, acceptanceType: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ohne_vorbehalt">Abnahme ohne Vorbehalt</SelectItem>
                        <SelectItem value="mit_vorbehalt">Abnahme mit Vorbehalt (Mängel vorhanden)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {protocolData.acceptanceType === 'mit_vorbehalt' && (
                    <div>
                      <Label className="text-sm">Festgestellte Mängel</Label>
                      <Textarea 
                        value={protocolData.defects}
                        onChange={(e) => setProtocolData({...protocolData, defects: e.target.value})}
                        placeholder="Auflistung der festgestellten Mängel..."
                        className="min-h-[80px]"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-sm">Bemerkungen</Label>
                    <Textarea 
                      value={protocolData.notes}
                      onChange={(e) => setProtocolData({...protocolData, notes: e.target.value})}
                      placeholder="Zusätzliche Anmerkungen..."
                      className="min-h-[60px]"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Side: Signatures */}
            <div className="space-y-4">
              {/* Customer Signature */}
              <Card>
                <CardHeader className="p-4 md:p-6 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Unterschrift Auftraggeber (Kunde) *
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0">
                  {protocolData.customerSignature ? (
                    <div className="space-y-3">
                      <div className="border rounded-lg p-2 bg-white">
                        <img src={protocolData.customerSignature} alt="Kundenunterschrift" className="max-h-32 mx-auto" />
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setProtocolData({...protocolData, customerSignature: null})}
                        className="w-full"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" /> Unterschrift löschen
                      </Button>
                    </div>
                  ) : isSigningCustomer ? (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden">
                        <canvas 
                          id="customerSignatureCanvas" 
                          width={400} 
                          height={150}
                          className="w-full h-[120px] sm:h-[150px] touch-none cursor-crosshair"
                          style={{ maxWidth: '100%' }}
                          ref={(canvas) => canvas && initSignatureCanvas('customerSignatureCanvas')}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-center">Mit Finger oder Maus unterschreiben</p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="default"
                          onClick={() => clearSignature('customerSignatureCanvas')}
                          className="flex-1 h-10 sm:h-9 text-sm"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Löschen
                        </Button>
                        <Button 
                          size="default"
                          onClick={() => saveSignature('customerSignatureCanvas', 'customer')}
                          className="flex-1 h-10 sm:h-9 text-sm bg-[#9B1B30] hover:bg-[#7a1526]"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Speichern
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={() => setIsSigningCustomer(true)}
                      className="w-full h-24 border-2 border-dashed"
                    >
                      <PenTool className="w-6 h-6 mr-2" />
                      Hier klicken zum Unterschreiben
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Contractor Signature */}
              <Card>
                <CardHeader className="p-4 md:p-6 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Unterschrift Auftragnehmer
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6 pt-0">
                  {protocolData.contractorSignature ? (
                    <div className="space-y-3">
                      <div className="border rounded-lg p-2 bg-white">
                        <img src={protocolData.contractorSignature} alt="Auftragnehmerunterschrift" className="max-h-32 mx-auto" />
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setProtocolData({...protocolData, contractorSignature: null})}
                        className="w-full"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" /> Unterschrift löschen
                      </Button>
                    </div>
                  ) : isSigningContractor ? (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden">
                        <canvas 
                          id="contractorSignatureCanvas" 
                          width={400} 
                          height={150}
                          className="w-full h-[120px] sm:h-[150px] touch-none cursor-crosshair"
                          style={{ maxWidth: '100%' }}
                          ref={(canvas) => canvas && initSignatureCanvas('contractorSignatureCanvas')}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-center">Mit Finger oder Maus unterschreiben</p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="default"
                          onClick={() => clearSignature('contractorSignatureCanvas')}
                          className="flex-1 h-10 sm:h-9 text-sm"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" /> Löschen
                        </Button>
                        <Button 
                          size="default"
                          onClick={() => saveSignature('contractorSignatureCanvas', 'contractor')}
                          className="flex-1 h-10 sm:h-9 text-sm bg-[#9B1B30] hover:bg-[#7a1526]"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Speichern
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={() => setIsSigningContractor(true)}
                      className="w-full h-24 border-2 border-dashed"
                    >
                      <PenTool className="w-6 h-6 mr-2" />
                      Hier klicken zum Unterschreiben
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <Card className="bg-[#9B1B30]/5 border-[#9B1B30]/20">
                <CardContent className="p-4 md:p-6 space-y-3">
                  <Button 
                    onClick={previewProtocol}
                    disabled={isPreviewingProtocol || !protocolCustomer}
                    variant="outline"
                    className="w-full h-12 sm:h-11 border-[#9B1B30] text-[#9B1B30] hover:bg-[#9B1B30]/10 text-sm sm:text-base"
                    data-testid="protocol-preview-btn"
                  >
                    {isPreviewingProtocol ? (
                      <><Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" /> Lädt Vorschau...</>
                    ) : (
                      <><Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> Vorschau anzeigen</>
                    )}
                  </Button>
                  <Button 
                    onClick={saveProtocol}
                    disabled={isSavingProtocol || !protocolCustomer || !protocolData.customerSignature}
                    className="w-full h-14 sm:h-12 bg-[#9B1B30] hover:bg-[#7a1526] text-base sm:text-lg"
                    data-testid="protocol-save-btn"
                  >
                    {isSavingProtocol ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Wird gespeichert...</>
                    ) : (
                      <><Save className="w-5 h-5 mr-2" /> Protokoll speichern</>
                    )}
                  </Button>
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    Das Protokoll wird als PDF gespeichert und kann per E-Mail versendet werden.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Protocol Preview Dialog - A4 Format */}
          <Dialog open={showProtocolPreview} onOpenChange={setShowProtocolPreview}>
            <DialogContent className="w-[95vw] max-w-[900px] max-h-[95vh] overflow-y-auto p-2 sm:p-4">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                  Protokoll Vorschau (A4-Format)
                </DialogTitle>
              </DialogHeader>
              <div 
                className="bg-gray-100 p-2 sm:p-4 rounded-lg overflow-x-auto"
                style={{ minHeight: '500px' }}
              >
                <div 
                  dangerouslySetInnerHTML={{ __html: protocolPreviewHtml }}
                  style={{ transform: 'scale(0.9)', transformOrigin: 'top center' }}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowProtocolPreview(false)} className="h-10 sm:h-9">
                  Schließen
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archive" className="space-y-4">
          <Card>
            <CardHeader className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-[#9B1B30]" />
                  Protokoll-Archiv
                </CardTitle>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Suche (Kunde, Projekt, Datum)..."
                    value={archiveSearch}
                    onChange={(e) => setArchiveSearch(e.target.value)}
                    className="pl-10"
                    data-testid="archive-search-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              {isLoadingArchive ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredProtocols.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {archiveSearch ? (
                    <>
                      <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Keine Protokolle gefunden für "{archiveSearch}"</p>
                    </>
                  ) : (
                    <>
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Noch keine Protokolle vorhanden</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setActiveTab('create')}
                      >
                        Erstes Protokoll erstellen
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredProtocols.map((protocol) => (
                    <div 
                      key={protocol.id} 
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg gap-3 sm:gap-4 hover:bg-gray-100 transition-colors"
                      data-testid={`protocol-item-${protocol.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-gray-400 shrink-0" />
                          <p className="font-medium text-sm sm:text-base truncate">{protocol.customer_name}</p>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 truncate pl-6">
                          {protocol.project_name || 'Kein Projektname'}
                        </p>
                        <div className="flex items-center gap-2 mt-1 pl-6">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{protocol.acceptance_date}</span>
                          {protocol.project_address && (
                            <span className="text-xs text-gray-400">• {protocol.project_address}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0 pl-6 sm:pl-0">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => downloadProtocolPdf(protocol.id)}
                          className="flex-1 sm:flex-none h-10 sm:h-9"
                          data-testid={`protocol-download-${protocol.id}`}
                        >
                          <Download className="w-4 h-4 mr-1 sm:mr-0" />
                          <span className="sm:hidden">PDF</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditEmailDialog(protocol)}
                          className="flex-1 sm:flex-none h-10 sm:h-9"
                          data-testid={`protocol-edit-email-${protocol.id}`}
                          title="E-Mail-Adresse bearbeiten"
                        >
                          <Pencil className="w-4 h-4 mr-1 sm:mr-0" />
                          <span className="sm:hidden">E-Mail</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => sendProtocolEmail(protocol.id, protocol.customer_email)}
                          className="flex-1 sm:flex-none h-10 sm:h-9"
                          data-testid={`protocol-email-${protocol.id}`}
                        >
                          <Send className="w-4 h-4 mr-1 sm:mr-0" />
                          <span className="sm:hidden">Senden</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => confirmDeleteProtocol(protocol)}
                          className="flex-1 sm:flex-none h-10 sm:h-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`protocol-delete-${protocol.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1 sm:mr-0" />
                          <span className="sm:hidden">Löschen</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Protokoll löschen?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Möchten Sie das Protokoll für <strong>{protocolToDelete?.customer_name}</strong> wirklich löschen?
              <br /><br />
              <span className="text-red-600 font-medium">Diese Aktion kann nicht rückgängig gemacht werden.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteProtocol}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Löschen...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Endgültig löschen</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={editEmailDialogOpen} onOpenChange={setEditEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#9B1B30]" />
              E-Mail-Adresse bearbeiten
            </DialogTitle>
            <DialogDescription className="pt-2">
              Geben Sie die E-Mail-Adresse für das Protokoll von <strong>{protocolToEdit?.customer_name}</strong> ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">E-Mail-Adresse</Label>
              <Input
                id="edit-email"
                type="email"
                placeholder="kunde@example.com"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                data-testid="edit-email-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEditEmailDialogOpen(false)}
                disabled={isSavingProtocol}
              >
                Abbrechen
              </Button>
              <Button 
                onClick={saveProtocolEmail}
                disabled={isSavingProtocol || !editEmail}
                className="bg-[#9B1B30] hover:bg-[#7A1625]"
              >
                {isSavingProtocol ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Speichern</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
